// scroll bar
import "simplebar-react/dist/simplebar.min.css";

// lightbox
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/styles.css";

// map
import "mapbox-gl/dist/mapbox-gl.css";
import "./utils/mapboxgl";

// editor
import "react-quill/dist/quill.snow.css";

// slick-carousel
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

// lazy image
import "react-lazy-load-image-component/src/effects/blur.css";

// ----------------------------------------------------------------------

import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
// @mui
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// react query
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Url params
import queryString from "query-string";
import { SnackbarProvider } from "src/components/snackbar";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
// routes
import Router from "./routes";
// theme
import ThemeProvider from "./theme";
// components
import React from "react";
import { API_URLS } from "./api/api-urls";
import { AuthProvider } from "./auth/auth-context";
import { MotionLazyContainer } from "./components/animate";
import { StyledChart } from "./components/chart";
import ScrollToTop from "./components/scroll-to-top";
import { setupFCMListener } from "./firebase/setup-fcm-listener";
import { mutationErrorHandler, queryErrorHandler } from "./utils/api/query-error-handler";
import { SettingsProvider } from "./components/settings/SettingsContext";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
// ----------------------------------------------------------------------

const newQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      mutationErrorHandler(error, variables, mutation);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      queryErrorHandler(error, query);
    },
  }),
});

export default function App() {
  React.useEffect(() => {
    setupFCMListener({
      onSuccess: () => {
        newQueryClient.invalidateQueries({ queryKey: [API_URLS.ACTIVITY] });
        newQueryClient.invalidateQueries({ queryKey: [API_URLS.NOTIFICATIONS.root] });
        newQueryClient.invalidateQueries({ queryKey: [API_URLS.NOTIFICATIONS.unreadCount] });
      },
    });
  }, []);

  return (
    <BrowserRouter>
      <QueryClientProvider client={newQueryClient}>
        <AuthProvider>
          <ThemeProvider>
            <HelmetProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <SettingsProvider>
                  <QueryParamProvider
                    adapter={ReactRouter6Adapter}
                    options={{
                      searchStringToObject: queryString.parse,
                      objectToSearchString: queryString.stringify,
                    }}
                  >
                    <ScrollToTop />
                    <MotionLazyContainer>
                      <ThemeProvider>
                        <SnackbarProvider>
                          <StyledChart />
                          <Router />
                        </SnackbarProvider>
                      </ThemeProvider>
                      <ReactQueryDevtools initialIsOpen={false} />
                    </MotionLazyContainer>
                  </QueryParamProvider>
                </SettingsProvider>
              </LocalizationProvider>
            </HelmetProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
